import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/About.css";
import "../styles/GuestbookAdmin.css";

const ACCESS_KEY = "father-paul-private-about";

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export default function GuestbookAdmin() {
  const [unlocked, setUnlocked] = useState(
    () => window.sessionStorage.getItem(ACCESS_KEY) === "unlocked",
  );
  const [password, setPassword] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [error, setError] = useState("");

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/guestbook-admin", {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (response.status === 401) {
        window.sessionStorage.removeItem(ACCESS_KEY);
        setUnlocked(false);
        return;
      }
      const data = await response.json();
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || "Could not load guestbook entries");
      }
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      setError(err.message || "Could not load guestbook entries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) loadEntries();
  }, [unlocked, loadEntries]);

  const counts = useMemo(() => ({
    pending: entries.filter((entry) => !entry.approved).length,
    published: entries.filter((entry) => Boolean(entry.approved)).length,
  }), [entries]);

  async function unlock(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/private-about/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) {
        setError("That password is not correct. Please try again.");
        return;
      }
      window.sessionStorage.setItem(ACCESS_KEY, "unlocked");
      setPassword("");
      setUnlocked(true);
    } catch {
      setError("The admin page could not be unlocked. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function setApproval(entry, approved) {
    setBusyId(entry.id);
    setError("");
    try {
      const response = await fetch("/api/guestbook-admin", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: entry.id, approved }),
      });
      const data = await response.json();
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || "Could not update the entry");
      }
      setEntries((current) => current.map((item) => (
        item.id === entry.id ? data.entry : item
      )));
    } catch (err) {
      setError(err.message || "Could not update the entry");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteEntry(entry) {
    if (confirmDeleteId !== entry.id) {
      setConfirmDeleteId(entry.id);
      return;
    }

    setBusyId(entry.id);
    setError("");
    try {
      const response = await fetch("/api/guestbook-admin", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: entry.id }),
      });
      const data = await response.json();
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || "Could not delete the entry");
      }
      setEntries((current) => current.filter((item) => item.id !== entry.id));
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err.message || "Could not delete the entry");
    } finally {
      setBusyId(null);
    }
  }

  if (!unlocked) {
    return (
      <section className="about-lock" role="main" aria-labelledby="guestbook-admin-lock-title">
        <div className="about-lock__card">
          <div className="about-lock__cross" aria-hidden="true">✝</div>
          <h1 id="guestbook-admin-lock-title">Guestbook Admin</h1>
          <p>Enter the family password to review and manage guestbook messages.</p>
          <form onSubmit={unlock} className="about-lock__form">
            <label htmlFor="guestbook-admin-password">Enter password to continue</label>
            <input
              id="guestbook-admin-password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setError("");
              }}
              autoComplete="off"
              autoFocus
            />
            {error && <p className="about-lock__error" role="alert">{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? "Checking…" : "Open Guestbook Admin"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="guestbook-admin" aria-labelledby="guestbook-admin-title">
      <header className="guestbook-admin__header">
        <span aria-hidden="true">✝</span>
        <p>Private family moderation</p>
        <h1 id="guestbook-admin-title">Guestbook Admin</h1>
        <div className="guestbook-admin__summary">
          <strong>{counts.pending} pending</strong>
          <strong>{counts.published} published</strong>
          <strong>{entries.length} total</strong>
        </div>
        <Link to="/guestbook">Return to public Guestbook</Link>
      </header>

      {error && <p className="guestbook-admin__error" role="alert">{error}</p>}
      {loading && <p className="guestbook-admin__empty">Loading guestbook entries…</p>}
      {!loading && entries.length === 0 && (
        <p className="guestbook-admin__empty">There are no guestbook entries yet.</p>
      )}

      <div className="guestbook-admin__entries">
        {entries.map((entry) => {
          const isApproved = Boolean(entry.approved);
          const isBusy = busyId === entry.id;
          return (
            <article className="guestbook-admin-entry" key={entry.id}>
              <div className="guestbook-admin-entry__topline">
                <span className={`guestbook-admin-entry__status ${isApproved ? "published" : "pending"}`}>
                  {isApproved ? "Published" : "Pending approval"}
                </span>
                <time dateTime={entry.created_at}>{formatDate(entry.created_at)}</time>
              </div>
              <blockquote>{entry.message}</blockquote>
              <div className="guestbook-admin-entry__author">
                <strong>{entry.name}</strong>
                {[entry.relationship, entry.location].filter(Boolean).join(" · ") && (
                  <span>{[entry.relationship, entry.location].filter(Boolean).join(" · ")}</span>
                )}
              </div>
              <div className="guestbook-admin-entry__actions">
                <button
                  type="button"
                  className={isApproved ? "unpublish" : "approve"}
                  onClick={() => setApproval(entry, !isApproved)}
                  disabled={isBusy}
                >
                  {isApproved ? "Unpublish" : "Approve and publish"}
                </button>
                <button
                  type="button"
                  className="delete"
                  onClick={() => deleteEntry(entry)}
                  onBlur={() => setConfirmDeleteId(null)}
                  disabled={isBusy}
                >
                  {confirmDeleteId === entry.id ? "Confirm delete" : "Delete"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
